import classNames from 'classnames';
import React from 'react';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import Button, { ButtonVariant } from '@/components/molecules/button/button';
import PriceDisplay from '@/components/molecules/price-display/price-display';
import variables from '@/components/organisms/theme-search-card/theme-search-card.variables';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { getHeightFromWidth } from '@/libraries/getters/get-heigh-from-width';
import useIsHovered from '@/libraries/hooks/use-is-hovered';
import styles from './theme-search-card.module.scss';

export interface ThemeSearchCardProps {
  /** The aspect ratio to use for the image */
  aspectRatio?: Content.Components['AspectRatio'];
  /** The class name to apply */
  className?: string;
  /** The text to show as a headline */
  headline?: Content.Components['SearchByThemeCardDto']['headline'];
  /** The url of the image to show */
  image: Content.Components['SearchByThemeCardDto']['image'];
  /** The link to show */
  link?: Content.Components['SearchByThemeCardDto']['link'];
  /** The splash to show on the card */
  splash?: Content.Components['SearchByThemeCardDto']['splash'];
  /** The text to show as a description */
  text?: Content.Components['SearchByThemeCardDto']['text'];
}

const ThemeSearchCard = ({
  aspectRatio,
  className,
  headline,
  image,
  link,
  splash,
  text,
}: ThemeSearchCardProps) => {
  const { t } = useI18n();
  const { bindRef, isHovered } = useIsHovered<HTMLElement>();

  const renderContent = () => (
    <React.Fragment>
      <div className={styles.imageWrapper}>
        <Image
          alt={image.altText}
          aspectRatio={aspectRatio}
          height={getHeightFromWidth({ aspectRatio, width: 390 })}
          src={image.url}
          srcSet={{
            tablet: {
              height: getHeightFromWidth({ aspectRatio, width: 312 }),
              width: 312,
            },
          }}
          unoptimized
          width={390}
        />

        {splash && <PriceDisplay {...splash} className={styles.priceDisplay} />}
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <Typography
            className={styles.headline}
            tag="h3"
            tagStyle="bodyLargeBold"
          >
            {headline}
          </Typography>

          <Typography tag="p" tagStyle="bodyMedium">
            {text}
          </Typography>
        </div>

        <Button
          aria-label={t('items.common.words.seeOffer')}
          hovered={isHovered}
          icon="ChevronRight"
          tabIndex={-1}
          variant={variables.cta.variant as ButtonVariant}
        />
      </div>
    </React.Fragment>
  );

  if (link && link.url) {
    return (
      <Link
        ref={bindRef}
        className={classNames(styles.component, className)}
        url={link.url}
      >
        {renderContent()}
      </Link>
    );
  }

  return (
    <div ref={bindRef} className={classNames(styles.component, className)}>
      {renderContent()}
    </div>
  );
};

ThemeSearchCard.displayName = 'ThemeSearchCard';

export default ThemeSearchCard;
